// NavLink — active-aware navigation link. New primitive (ADR-014 sign-off).
//
// Wraps React Router 7's <NavLink>, which sets `aria-current="page"` on the
// active link automatically — or, when the host knows better (a domain lit by
// its AREA's routes: `config/nav` `isOnDomain`), the plain router <Link> with
// the class AND `aria-current` from the host's `active`. This primitive adds the
// brand-aware active styling + the focus ring + two layout variants so every nav
// surface (top-bar domains, admin sidebar) shares one accessible, theme-correct
// link. Never inline a raw <a> / styled <Link> for navigation that needs an
// active state.
//
// Nav is INDICATOR-led, not fill-led (ADR-034): brand lives on the active item's
// indicator, not on a hover slab. See DESIGN-TOKENS §Interaction states.

import { Link as RouterLink, NavLink as RouterNavLink, type NavLinkProps as RouterNavLinkProps } from 'react-router-dom';
import { forwardRef, type ReactNode, type AnchorHTMLAttributes } from 'react';

export type NavLinkVariant = 'topbar' | 'sidebar' | 'tabs';

export interface NavLinkProps extends Omit<RouterNavLinkProps, 'className' | 'children' | 'style'> {
  variant?: NavLinkVariant;
  children: ReactNode;
  /** Extra classes merged after the variant classes. */
  className?: string;
  /** External destination (leaves the SPA, e.g. the backend Swagger UI): renders a
   *  plain `<a target="_blank">` with no active state. `to` is used as the href. */
  external?: boolean;
  /** The host's word on the active face, when the router's path match is not the ground — a domain lit by its
   *  AREA's routes (`config/nav` `isOnDomain`): the class and `aria-current` both follow it. Undefined = the router
   *  decides. */
  active?: boolean;
}

// NAV-STATE RULE (ADR-034 — nav items are their own state class):
//  • Topbar = UNDERLINE-led, NO fill. Active = brand underline (::after,
//             --color-primary-display) + bold + primary text. Hover = a faint
//             NEUTRAL underline preview (--color-border-strong) + text brightens.
//  • Sidebar = LEFT-BAR-led. Active = brand left-bar (::before,
//             --color-primary-display) + a subtle --color-primary-light fill +
//             bold. Hover = the neutral row state-layer (--color-bg-hover).
//  • Tabs    = the SAME treatment as Topbar (decoupled ::after underline, brand
//             active, neutral hover-preview) for the entity-surface tab bar (URL
//             sub-routes → navigation, NOT the in-page Tabs primitive). Only the
//             height differs (h-11 page tab vs h-14 top bar), so the entity tabs
//             and the global nav read as one system. ≥44px tall (SC 2.5.8).
//  • Focus  = solid brand inset ring (focus-ring-nav).
//  • Items are SQUARE-cornered so the full-span indicators sit flush and the
//             focus ring is a clean contained rect (the indicator is a decoupled
//             pseudo-element — Material/Radix Tabs, GitHub UnderlineNav).
// All colours are ThemeProvider-derived tokens, so per-project brand + dark mode
// are correct by construction.
const focusRing = 'outline-none focus-ring-nav';

const variantClass: Record<NavLinkVariant, { base: string; active: string; inactive: string }> = {
  topbar: {
    base:
      `relative inline-flex items-center gap-2 px-3 h-14 text-sm font-medium transition-colors ${focusRing} ` +
      `after:content-[''] after:absolute after:bottom-0 after:inset-x-0 after:h-0.5 after:transition-colors`,
    active: 'text-[var(--color-text-primary)] font-semibold after:bg-[var(--color-primary-display)]',
    inactive:
      'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:after:bg-[var(--color-border-strong)]',
  },
  sidebar: {
    base:
      `relative flex items-center gap-3 px-3 py-2 min-h-[2.25rem] text-sm transition-colors ${focusRing} ` +
      `before:content-[''] before:absolute before:start-0 before:inset-y-0 before:w-0.5 before:transition-colors`,
    active:
      'bg-[var(--color-primary-light)] text-[var(--color-text-primary)] font-semibold ' +
      'before:bg-[var(--color-primary-display)]',
    inactive:
      'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]',
  },
  // Mirrors `topbar` exactly (the ::after underline overlaps the tab bar's own
  // border-b), at page-tab height — so the entity tabs and the global nav are one
  // visual system. The active brand underline is `--color-primary-display` (never
  // neutral); hover is the faint neutral preview, per ADR-034.
  tabs: {
    // min-h (not fixed h) + the parent flex row's default `items-stretch` lets a
    // tab fill a taller bar (the condensed entity bar) so the ::after underline
    // always lands on the bar's bottom edge, never floating mid-row. ≥44px floor.
    base:
      `relative inline-flex items-center gap-1.5 px-3 min-h-11 text-sm font-medium whitespace-nowrap transition-colors ${focusRing} ` +
      `after:content-[''] after:absolute after:bottom-0 after:inset-x-0 after:h-0.5 after:transition-colors rounded-t-sm`,
    active: 'text-[var(--color-text-primary)] font-semibold after:bg-[var(--color-primary-display)]',
    inactive:
      'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:after:bg-[var(--color-border-strong)]',
  },
};

// forwardRef so a collapsed-rail NavLink can be a React Aria Tooltip trigger
// (via <Focusable>, which needs the underlying <a> ref).
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { variant = 'topbar', className = '', children, external, to, replace, active, ...props },
  ref,
) {
  const v = variantClass[variant];
  // A tab is a FACE of one record, not a place: the `tabs` variant REPLACES the history entry by default (ADR-037
  // §2 — Back returns to the page that led in, never to the previous tab), so no sub-route tab bar can forget it.
  const replaces = replace ?? variant === 'tabs';
  // External links leave the SPA (no router, no active state) — a plain anchor in
  // a new tab. `rel="noopener noreferrer"` per target=_blank security hygiene.
  if (external) {
    return (
      <a
        ref={ref}
        href={to as string}
        target="_blank"
        rel="noopener noreferrer"
        className={`${v.base} ${v.inactive} ${className}`}
        {...(props as AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    );
  }
  // The host's word: the router's <NavLink> would announce its OWN match (`aria-current` is its, a prop cannot force
  // it), so the plain <Link> carries both the class and the announcement from `active`.
  if (active !== undefined) {
    const { end: _end, caseSensitive: _caseSensitive, ...rest } = props;
    return (
      <RouterLink ref={ref} to={to} replace={replaces} aria-current={active ? 'page' : undefined} className={`${v.base} ${active ? v.active : v.inactive} ${className}`} {...rest}>
        {children}
      </RouterLink>
    );
  }
  return (
    <RouterNavLink ref={ref} to={to} replace={replaces} className={({ isActive }) => `${v.base} ${isActive ? v.active : v.inactive} ${className}`} {...props}>
      {children}
    </RouterNavLink>
  );
});
